
import axios from 'axios';
import { Role } from '../types';

// Use Vite env when provided, otherwise default to proxied path behind NGINX
const API_BASE = import.meta.env.VITE_API_URL || '/meos/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

export const dashboardApi = {
  // Live operational KPIs (revenue, hours, readiness)
  getSummary: async () => {
    const response = await api.get('/dashboard/summary');
    return response.data;
  },

  // Latest telemetry for entire fleet
  getFleetPositions: async () => {
    const response = await api.get('/fleet/positions');
    return response.data;
  },

  // Critical operational alerts (AOG, expiries)
  getAlerts: async () => {
    const response = await api.get('/dashboard/alerts');
    return response.data;
  }
};

// Interceptor to add JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ma_jwt');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const proxyAvatarUrl = (avatarUrl: string | undefined): string => {
  if (!avatarUrl) return '';
  if (!avatarUrl.startsWith('http')) return avatarUrl;
  const apiBase = import.meta.env.VITE_API_URL || '/meos/api';
  const jwt = localStorage.getItem('ma_jwt') || '';
  return `${apiBase}/auth/zoho-photo?url=${encodeURIComponent(avatarUrl)}${jwt ? `&jwt=${encodeURIComponent(jwt)}` : ''}`;
};

export const proxyGooglePhotoUrl = (photoUrl: string | undefined, cacheKey?: string): string => {
  if (!photoUrl) return '';
  if (!photoUrl.startsWith('http')) return photoUrl;
  const apiBase = import.meta.env.VITE_API_URL || '/meos/api';
  const version = String(cacheKey || '').trim();
  return `${apiBase}/auth/google-photo?url=${encodeURIComponent(photoUrl)}${version ? `&v=${encodeURIComponent(version)}` : ''}`;
};

const normalizeFrontendRole = (backendRole: string | undefined): Role => {
  const roleMap: Record<string, Role> = {
    ADMIN: 'ADMIN',
    // Treat SUPER_ADMIN as full frontend admin access.
    SUPER_ADMIN: 'ADMIN',
    DFO: 'DFO',
    CP: 'CP',
    PILOT: 'PILOT',
    FIRST_OFFICER: 'FIRST_OFFICER',
    DISPATCHER: 'FLIGHT_PLANNER',
    CUSTOMER_SERVICE: 'RESERVATIONS',
    READONLY: 'RESERVATIONS',
    ACCOUNTANT: 'ACCOUNTS',
    ENGINEER: 'GROUND_OPS',
    CEO: 'CEO',
    FLIGHT_PLANNER: 'FLIGHT_PLANNER',
    RESERVATIONS: 'RESERVATIONS',
    ACCOUNTS: 'ACCOUNTS',
    GROUND_OPS: 'GROUND_OPS',
  };
  return roleMap[String(backendRole || '').toUpperCase()] || 'RESERVATIONS';
};

const parseInboundInboxKeys = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(value.map((item) => String(item || '').trim().toLowerCase()).filter(Boolean))
    );
  }
  return Array.from(
    new Set(
      String(value || '')
        .split(/[,\n;]/g)
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean)
    )
  );
};

export const authApi = {
  validateSession: async () => {
    const response = await api.get('/auth/me');
    const user = response.data.user;
    // Proxy avatar URL if it exists (for Zoho People photos)
    if (user) {
      const photoUrl = user.avatar || user.photoUrl;
      if (photoUrl && photoUrl.trim() !== '') {
        user.avatar = proxyAvatarUrl(photoUrl);
        console.log('Profile picture URL:', user.avatar);
      } else {
        console.log('No photoUrl found for user:', user.email);
        user.avatar = null;
      }
      user.role = normalizeFrontendRole(user.role);
      user.allowedInboundInboxKeys = parseInboundInboxKeys((user as any).allowedInboundInboxKeys);
    }
    return user;
  },
  loginAdmin: async (payload: any) => {
    const response = await api.post('/auth/login', payload);
    if (response?.data?.user) {
      response.data.user.role = normalizeFrontendRole(response.data.user.role);
      response.data.user.allowedInboundInboxKeys = parseInboundInboxKeys((response.data.user as any).allowedInboundInboxKeys);
    }
    return response.data;
  }
};

export default api;
